import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { QRCodeCanvas } from 'qrcode.react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Plus, 
  Mail, 
  Shield, 
  User, 
  Eye, 
  Power,
  PowerOff,
  X,
  Send,
  Copy,
  Clock,
  MessageCircle,
  Link2,
  Share2
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_INFO = {
  super_admin: {
    label: 'Super Admin',
    description: 'Acceso total a todas las instituciones',
    icon: Shield,
    color: 'bg-red-100 text-red-800',
    badgeColor: 'bg-red-600'
  },
  admin: {
    label: 'Administrador',
    description: 'Gestión completa de la institución',
    icon: Shield,
    color: 'bg-blue-100 text-blue-800',
    badgeColor: 'bg-blue-600'
  },
  therapist: {
    label: 'Terapeuta',
    description: 'Acceso completo a pacientes y reportes',
    icon: User,
    color: 'bg-green-100 text-green-800',
    badgeColor: 'bg-green-600'
  },
  read_only: {
    label: 'Solo Lectura',
    description: 'Ver datos sin editar',
    icon: Eye,
    color: 'bg-gray-100 text-gray-800',
    badgeColor: 'bg-gray-600'
  }
};

const AdminPanelPage = () => {
  const [users, setUsers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({
    name: '',
    email: '',
    role: 'therapist',
    phone: '',
    send_email: true
  });
  const [createdInvite, setCreatedInvite] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [usersRes, rolesRes, invitationsRes] = await Promise.all([
        axios.get(`${API}/admin/users`, { withCredentials: true }),
        axios.get(`${API}/admin/roles`, { withCredentials: true }),
        axios.get(`${API}/invitations/pending`, { withCredentials: true })
      ]);
      setUsers(usersRes.data);
      setRoles(rolesRes.data.roles);
      setInvitations(invitationsRes.data);
    } catch (error) {
      const errorMsg = error.response?.data?.detail;
      if (Array.isArray(errorMsg)) {
        toast.error(errorMsg.map(e => e.msg).join(', '));
      } else {
        toast.error(errorMsg || 'Error al cargar datos');
      }
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Memoize filtered roles to avoid recomputation on every render
  const assignableRoles = useMemo(() => 
    roles.filter(r => r.can_assign),
    [roles]
  );

  const handleInvite = async (e) => {
    e.preventDefault();
    try {
      const { data } = await axios.post(
        `${API}/invitations/invite`,
        inviteForm,
        { withCredentials: true }
      );

      setInviteDialogOpen(false);
      if (data.token) {
        // Email not sent (or failed): show the code so it can be shared manually
        setCreatedInvite({ id: data.id, token: data.token, email: inviteForm.email, name: inviteForm.name, phone: inviteForm.phone });
      } else {
        toast.success('Invitación enviada por email');
      }

      setInviteForm({ name: '', email: '', role: 'therapist', phone: '', send_email: true });
      fetchData();
    } catch (error) {
      const errorMsg = error.response?.data?.detail;
      if (Array.isArray(errorMsg)) {
        toast.error(errorMsg.map(e => e.msg).join(', '));
      } else {
        toast.error(errorMsg || 'Error al invitar usuario');
      }
    }
  };

  const markShared = async (id) => {
    if (!id) return;
    try {
      await axios.post(`${API}/invitations/${id}/mark-shared`, {}, { withCredentials: true });
      fetchData();
    } catch (error) {
      // non-blocking
    }
  };

  const buildWhatsappUrl = (phone, message) => {
    const digits = (phone || '').replace(/\D/g, '');
    return digits
      ? `https://wa.me/${digits}?text=${encodeURIComponent(message)}`
      : `https://wa.me/?text=${encodeURIComponent(message)}`;
  };

  const inviteLink = createdInvite
    ? `${window.location.origin}/accept-invite?token=${createdInvite.token}`
    : '';
  const shareMessage = createdInvite
    ? `Hola ${createdInvite.name}, has sido invitado/a a REKORD. Crea tu cuenta aquí: ${inviteLink} (o usa el código: ${createdInvite.token})`
    : '';

  const handleToggleActive = async (userId, currentStatus) => {
    const action = currentStatus ? 'desactivar' : 'activar';
    if (!window.confirm(`¿Estás seguro de ${action} este usuario?`)) return;
    
    try {
      await axios.put(
        `${API}/admin/users/${userId}`,
        { is_active: !currentStatus },
        { withCredentials: true }
      );
      toast.success(`Usuario ${action === 'desactivar' ? 'desactivado' : 'activado'}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Error al ${action} usuario`);
    }
  };

  const handleCancelInvitation = async (invitationId) => {
    if (!window.confirm('¿Cancelar esta invitación?')) return;
    
    try {
      await axios.delete(
        `${API}/invitations/${invitationId}`,
        { withCredentials: true }
      );
      toast.success('Invitación cancelada');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cancelar invitación');
    }
  };

  const handleResendInvitation = async (invitationId) => {
    try {
      await axios.post(
        `${API}/invitations/${invitationId}/resend`,
        {},
        { withCredentials: true }
      );
      toast.success('Invitación reenviada');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al reenviar invitación');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Código copiado al portapapeles');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <p>Cargando...</p>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold mb-2">Panel de Administración</h1>
          <p className="text-gray-600">
            Invita usuarios, gestiona roles y controla accesos
          </p>
        </div>
        <Button 
          onClick={() => setInviteDialogOpen(true)} 
          size="lg"
          style={{ backgroundColor: '#5A7D6C' }}
          className="shadow-lg hover:shadow-xl transition-shadow"
        >
          <Mail className="mr-2 h-5 w-5" />
          Invitar Nuevo Profesional
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Usuarios Activos</p>
              <p className="text-2xl font-bold">{users.filter(u => u.is_active !== false).length}</p>
            </div>
            <div className="h-12 w-12 bg-green-100 rounded-full flex items-center justify-center">
              <User className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Invitaciones Pendientes</p>
              <p className="text-2xl font-bold">{invitations.filter(i => !i.is_expired).length}</p>
            </div>
            <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center">
              <Mail className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Administradores</p>
              <p className="text-2xl font-bold">
                {users.filter(u => u.role === 'admin' && u.is_active !== false).length}
              </p>
            </div>
            <div className="h-12 w-12 bg-purple-100 rounded-full flex items-center justify-center">
              <Shield className="h-6 w-6 text-purple-600" />
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Terapeutas</p>
              <p className="text-2xl font-bold">
                {users.filter(u => u.role === 'therapist' && u.is_active !== false).length}
              </p>
            </div>
            <div className="h-12 w-12 bg-orange-100 rounded-full flex items-center justify-center">
              <User className="h-6 w-6 text-orange-600" />
            </div>
          </div>
        </Card>
      </div>

      <Tabs defaultValue="users" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="users">Usuarios ({users.length})</TabsTrigger>
          <TabsTrigger value="invitations">
            Invitaciones ({invitations.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="users" className="mt-6">
          <Card className="p-6">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4">Usuario</th>
                    <th className="text-left py-3 px-4">Rol</th>
                    <th className="text-left py-3 px-4">Estado</th>
                    <th className="text-left py-3 px-4">Fecha Creación</th>
                    <th className="text-left py-3 px-4">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => {
                    const roleInfo = ROLE_INFO[user.role] || {};
                    const Icon = roleInfo.icon || User;
                    const isActive = user.is_active !== false;
                    
                    return (
                      <tr key={user.id} className="border-b hover:bg-gray-50">
                        <td className="py-3 px-4">
                          <div>
                            <p className="font-medium">{user.name}</p>
                            <p className="text-sm text-gray-500">{user.email}</p>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <Badge className={roleInfo.color}>
                            <Icon className="h-3 w-3 mr-1" />
                            {roleInfo.label || user.role}
                          </Badge>
                        </td>
                        <td className="py-3 px-4">
                          {isActive ? (
                            <Badge className="bg-green-100 text-green-800">
                              <Power className="h-3 w-3 mr-1" />
                              Activo
                            </Badge>
                          ) : (
                            <Badge className="bg-red-100 text-red-800">
                              <PowerOff className="h-3 w-3 mr-1" />
                              Inactivo
                            </Badge>
                          )}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600">
                          {user.created_at ? new Date(user.created_at).toLocaleDateString() : '-'}
                        </td>
                        <td className="py-3 px-4">
                          <Button
                            variant={isActive ? "outline" : "default"}
                            size="sm"
                            onClick={() => handleToggleActive(user.id, isActive)}
                          >
                            {isActive ? (
                              <>
                                <PowerOff className="h-4 w-4 mr-1" />
                                Desactivar
                              </>
                            ) : (
                              <>
                                <Power className="h-4 w-4 mr-1" />
                                Activar
                              </>
                            )}
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {users.length === 0 && (
                <p className="text-center py-8 text-gray-500">No hay usuarios</p>
              )}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="invitations" className="mt-6">
          <Card className="p-6">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4">Email</th>
                    <th className="text-left py-3 px-4">Rol</th>
                    <th className="text-left py-3 px-4">Estado</th>
                    <th className="text-left py-3 px-4">Expira</th>
                    <th className="text-left py-3 px-4">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {invitations.map((invitation) => {
                    const roleInfo = ROLE_INFO[invitation.role] || {};
                    const Icon = roleInfo.icon || User;
                    
                    return (
                      <tr key={invitation.id} className="border-b hover:bg-gray-50">
                        <td className="py-3 px-4">
                          <div>
                            <p className="font-medium">{invitation.name}</p>
                            <p className="text-sm text-gray-500">{invitation.email}</p>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <Badge className={roleInfo.color}>
                            <Icon className="h-3 w-3 mr-1" />
                            {roleInfo.label}
                          </Badge>
                        </td>
                        <td className="py-3 px-4">
                          {invitation.is_expired ? (
                            <Badge className="bg-red-100 text-red-800">
                              <Clock className="h-3 w-3 mr-1" />
                              Expirada
                            </Badge>
                          ) : invitation.shared ? (
                            <Badge className="bg-green-100 text-green-800" data-testid={`invitation-shared-${invitation.id}`}>
                              <Send className="h-3 w-3 mr-1" />
                              Enviada
                            </Badge>
                          ) : (
                            <Badge className="bg-yellow-100 text-yellow-800">
                              <Clock className="h-3 w-3 mr-1" />
                              Pendiente
                            </Badge>
                          )}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600">
                          {new Date(invitation.expires_at).toLocaleDateString()}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex space-x-2">
                            {invitation.token && (
                              <Button
                                variant="outline"
                                size="sm"
                                title="Ver enlace, código y QR para compartir"
                                onClick={() => setCreatedInvite({ id: invitation.id, token: invitation.token, email: invitation.email, name: invitation.name, phone: invitation.phone })}
                                data-testid={`share-invitation-${invitation.id}`}
                              >
                                <Share2 className="h-4 w-4" />
                              </Button>
                            )}
                            {invitation.token && (
                              <Button
                                variant="outline"
                                size="sm"
                                title="Copiar enlace de acceso"
                                onClick={() => {
                                  copyToClipboard(`${window.location.origin}/accept-invite?token=${invitation.token}`);
                                  markShared(invitation.id);
                                }}
                                data-testid={`copy-link-invitation-${invitation.id}`}
                              >
                                <Link2 className="h-4 w-4" />
                              </Button>
                            )}
                            <Button
                              variant="outline"
                              size="sm"
                              title="Reenviar por email"
                              onClick={() => handleResendInvitation(invitation.id)}
                            >
                              <Send className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              title="Cancelar invitación"
                              onClick={() => handleCancelInvitation(invitation.id)}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {invitations.length === 0 && (
                <p className="text-center py-8 text-gray-500">
                  No hay invitaciones pendientes
                </p>
              )}
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Invite Dialog */}
      <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" style={{ color: '#5A7D6C' }} />
              Invitar Nuevo Profesional
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleInvite} className="space-y-4">
            <div>
              <Label htmlFor="name">Nombre Completo</Label>
              <Input
                id="name"
                value={inviteForm.name}
                onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
                required
                placeholder="Juan Pérez"
              />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={inviteForm.email}
                onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                required
                placeholder="juan@example.com"
              />
            </div>
            <div>
              <Label htmlFor="phone">Teléfono / WhatsApp (opcional)</Label>
              <Input
                id="phone"
                value={inviteForm.phone}
                onChange={(e) => setInviteForm({ ...inviteForm, phone: e.target.value })}
                placeholder="+54 9 11 1234 5678"
                data-testid="invite-phone-input"
              />
              <p className="text-xs text-gray-500 mt-1">
                Con código de país para abrir WhatsApp directo en su chat.
              </p>
            </div>
            <div>
              <Label htmlFor="role">Rol</Label>
              <Select
                value={inviteForm.role}
                onValueChange={(value) => setInviteForm({ ...inviteForm, role: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {assignableRoles.map((role) => (
                    <SelectItem key={role.role} value={role.role}>
                      {ROLE_INFO[role.role]?.label || role.role}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                {ROLE_INFO[inviteForm.role]?.description}
              </p>
            </div>
            <div className="flex items-center space-x-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <input
                type="checkbox"
                id="send_email"
                checked={inviteForm.send_email}
                onChange={(e) => setInviteForm({ ...inviteForm, send_email: e.target.checked })}
                className="rounded h-4 w-4"
              />
              <div className="flex-1">
                <Label htmlFor="send_email" className="font-medium text-blue-900 cursor-pointer">
                  Enviar invitación por email automáticamente
                </Label>
                <p className="text-xs text-blue-700 mt-0.5">
                  El profesional recibirá un correo con su código de invitación via Resend
                </p>
              </div>
            </div>
            <div className="flex space-x-2 pt-4">
              <Button 
                type="submit" 
                className="flex-1"
                style={{ backgroundColor: '#5A7D6C' }}
              >
                <Mail className="mr-2 h-4 w-4" />
                Enviar Invitación
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setInviteDialogOpen(false)}
              >
                Cancelar
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Invitation Created - Manual Share Dialog */}
      <Dialog open={!!createdInvite} onOpenChange={(open) => { if (!open) setCreatedInvite(null); }}>
        <DialogContent className="max-w-md" data-testid="invite-code-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" style={{ color: '#5A7D6C' }} />
              Invitación creada
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Comparte estos datos con <strong>{createdInvite?.name}</strong> ({createdInvite?.email}) por
              WhatsApp, email o el medio que prefieras. Podrá crear su cuenta con el enlace o el código.
            </p>

            <div>
              <Label className="text-xs text-gray-500">Enlace de invitación</Label>
              <div className="flex gap-2 mt-1">
                <Input readOnly value={inviteLink} className="font-mono text-xs" data-testid="invite-link-input" />
                <Button type="button" variant="outline" onClick={() => copyToClipboard(inviteLink)} title="Copiar enlace">
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div>
              <Label className="text-xs text-gray-500">Código de invitación</Label>
              <div className="flex gap-2 mt-1">
                <Input readOnly value={createdInvite?.token || ''} className="font-mono text-xs" data-testid="invite-code-input" />
                <Button type="button" variant="outline" onClick={() => copyToClipboard(createdInvite?.token)} title="Copiar código">
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
              <p className="text-xs text-emerald-800 mb-2">Mensaje listo para enviar:</p>
              <p className="text-xs text-emerald-900">{shareMessage}</p>
              <div className="flex flex-wrap gap-2 mt-2">
                <Button
                  type="button"
                  size="sm"
                  style={{ backgroundColor: '#25D366' }}
                  onClick={() => {
                    window.open(buildWhatsappUrl(createdInvite?.phone, shareMessage), '_blank');
                    markShared(createdInvite?.id);
                  }}
                  data-testid="whatsapp-share-btn"
                >
                  <MessageCircle className="h-4 w-4 mr-1" />
                  Enviar por WhatsApp
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => { copyToClipboard(shareMessage); markShared(createdInvite?.id); }}
                  data-testid="copy-share-message-btn"
                >
                  <Copy className="h-4 w-4 mr-1" />
                  Copiar mensaje
                </Button>
              </div>
            </div>

            <div className="flex flex-col items-center p-3 border rounded-lg">
              <p className="text-xs text-gray-500 mb-2">O que lo escanee desde su móvil:</p>
              {createdInvite && (
                <div className="bg-white p-2 rounded" data-testid="invite-qr">
                  <QRCodeCanvas value={inviteLink} size={140} includeMargin />
                </div>
              )}
            </div>

            <p className="text-xs text-gray-400">
              Válido 7 días. Guarda esto ahora: el código no se vuelve a mostrar.
            </p>

            <Button type="button" variant="outline" className="w-full" onClick={() => setCreatedInvite(null)}>
              Cerrar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPanelPage;
